import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { setTrackActionImpl } from "@eventify/shared/lib/trackAction";

const FLUSH_INTERVAL_MS = 1500;
const MAX_BUFFER = 50;

export type EventType = "screen" | "click" | "action";

interface BufferContext {
  uid: string | null;
  vendorId: string | null;
  profileId: string | null;
  profileName: string | null;
  enabled: boolean;
}

interface PendingEvent {
  uid: string;
  type: EventType;
  name: string;
  platform: "web";
  vendorId: string | null;
  profileId: string | null;
  profileName: string | null;
  route: string | null;
  metadata: Record<string, unknown> | null;
  capturedAtMs: number;
}

const context: BufferContext = {
  uid: null,
  vendorId: null,
  profileId: null,
  profileName: null,
  enabled: false,
};

let queue: PendingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export function setEventBufferContext(next: Partial<BufferContext>) {
  Object.assign(context, next);
}

export function enqueueEvent(input: {
  type: EventType;
  name: string;
  metadata?: Record<string, unknown> | null;
}) {
  if (!context.enabled || !context.uid) return;
  if (!input.name) return;

  const event: PendingEvent = {
    uid: context.uid,
    type: input.type,
    name: input.name.slice(0, 120),
    platform: "web",
    vendorId: context.vendorId,
    profileId: context.profileId,
    profileName: context.profileName,
    route: typeof window !== "undefined" ? window.location.pathname : null,
    metadata: input.metadata ?? null,
    capturedAtMs: Date.now(),
  };

  queue.push(event);
  if (queue.length >= MAX_BUFFER) {
    void flush();
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
  }
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];

  await Promise.all(
    batch.map((e) =>
      addDoc(collection(db, "userActivity", e.uid, "events"), {
        ts: serverTimestamp(),
        type: e.type,
        name: e.name,
        platform: e.platform,
        vendorId: e.vendorId,
        profileId: e.profileId,
        profileName: e.profileName,
        route: e.route,
        metadata: e.metadata,
      }).catch(() => {})
    )
  );
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    void flush();
  });
  window.addEventListener("pagehide", () => {
    void flush();
  });
}

setTrackActionImpl((name, metadata) => {
  enqueueEvent({ type: "action", name, metadata: metadata ?? null });
});
